import React, { useState, useEffect } from 'react';
import {
  Flex,
  Text,
  Button,
  useToast,
  Center,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import { EditIcon, CloseIcon } from '@chakra-ui/icons';
import { Formik, Form } from 'formik';
import { useAuth } from '../../../../../auth-context/auth.context';
import CompaniesApi from 'api/companies';
import PinCodeApi from 'api/pincode';
import Card from 'components/card/Card';
import SelectField from '../../../../../components/formComponents/selectField.js';
import InputField from '../../../../../components/formComponents/inputField.js';
import UpperCaseChange from 'components/formComponents/upperCaseChange.js';
import { usePermissions } from 'contexts/PermissionContext';
import { Modules } from 'config/constant';
import { UpdateCompanyFormValidationSchema } from 'formValidations/updateCompanyFormValidations';
import PageError from 'components/nopermission/PageError';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';
import { isValidUUID } from 'utils/fetchUtils';

export default function About(props) {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const toastIdRef = React.useRef();
  const { user } = useAuth();
  const [disabled, setDisabled] = useState(true);
  const [inpDisable, setInpDisable] = useState(true);
  const [companyData, setCompanyData] = useState({});
  const enable = () => setDisabled(!disabled);
  const disable = () => setDisabled(true);
  const { getPermissionsForModule } = usePermissions();
  const [companyPermissions, setCompanyPermissions] = useState(null);
  const [fetchCompanyDataError, setFetchCompanyDataError] = useState(false);

  const id = IdFromUrl(window.location.href);
  function IdFromUrl(url) {
    // Split the URL by slashes
    const parts = url.split('/');

    // The last part of the URL is the ID
    const id = parts[parts.length - 1];

    return id;
  }

  const FetchCompanyData = async () => {
    if (!isValidUUID(id)) {
      setFetchCompanyDataError(true);
      return;
    }
    try {
      setLoading(true);
      let response = await CompaniesApi.getById(id);
      response = response.data;
      setLoading(false);
      if (response.status_code === 200) {
        setFetchCompanyDataError(false);
        setCompanyData(response?.data);
      } else {
        setFetchCompanyDataError(true);
        setCompanyData({});
      }
    } catch (err) {
      setFetchCompanyDataError(true);
      console.log(err);
    }
  };

  useEffect(() => {
    const companyPermissions = getPermissionsForModule([Modules.Company]);
    setCompanyPermissions(companyPermissions);
  }, [getPermissionsForModule]);

  useEffect(() => {
    if (companyPermissions?.read) {
      FetchCompanyData();
    }
  }, [companyPermissions]);

  const initialValues = {
    name: companyData.name || '',
    email: companyData.email || '',
    phoneNumber: companyData.phone_number || '',
    countryCode: companyData.country_code || '',
    addressLine1: companyData.address_line1 || '',
    addressLine2: companyData.address_line2 || '',
    city: companyData.city,
    state: companyData.state,
    country: companyData.country,
    landmarks: companyData.landmarks || '',
    pincode: companyData.pincode || '',
    panNumber: companyData.pan_number || '',
    gstNumber: companyData.gst_number || '',
    status: companyData.status || '',
  };

  const handleUpdate = async (values, { setSubmitting }) => {
    console.log('Update company called');
    if (toastIdRef.current) {
      toast.close(toastIdRef.current);
    }
    try {
      let response = await CompaniesApi.Update({
        id: id,
        ...values,
      });
      response = response.data;
      if (response.status_code === 200) {
        toastIdRef.current = toast({
          title: response.message || 'Successfully Updated',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else if (response.status_code !== 200) {
        toastIdRef.current = toast({
          title: response.message || 'Error Updating',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error) {
      toastIdRef.current = toast({
        title: 'Unable to Update',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      console.log(error);
    } finally {
      setSubmitting(false);
      setDisabled(true);
    }
  };
  console.log('Company Permissions', companyPermissions);
  const GetPincode = async (e, setFieldValue) => {
    console.log('GetPincode called', e);

    try {
      let response = await PinCodeApi.Get({ pincode: e });
      response = response.data;
      console.log(response);
      if (
        response.status_code === 200 &&
        Object.keys(response.data).length !== 0
      ) {
        setFieldValue('country', response.data.country);
        setFieldValue('state', response.data.state);
        setFieldValue('city', response.data.city);
        setInpDisable(false);
      } else {
        toastIdRef.current = toast({
          title:
            'Pincode not found! Please recheck pincode or fill City, State and Country manually.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        setInpDisable(false);
      }
    } catch (error) {
      console.log('Here is Error', error);
      setInpDisable(false);
    }
  };

  if (!companyPermissions?.read) {
    return <NoPermissionsMessage module={Modules.Company} />;
  }

  return loading ? (
    <Center
      mt='10rem'
      width='100%'
      // bgColor='red'
    >
      <Spinner size='xl' />
    </Center>
  ) : fetchCompanyDataError ? (
    <PageError />
  ) : (
    <>
      <Flex
        alignItems='center'
        justifyContent='space-between'
        mt={10}
        mb={10}>
        <Text
          fontSize='3xl'
          fontWeight='400'>
          About - {companyData.name}
        </Text>
        {disabled ? (
          <Tooltip
            label={
              !companyPermissions?.update
                ? 'You do not have permission to edit'
                : undefined
            }
            placement='top'
            shouldWrapChildren
            isDisabled={companyPermissions?.update} // Disables Tooltip if update permission exists
          >
            <EditIcon
              ml='5'
              fontSize='large'
              color={
                companyPermissions?.update && companyPermissions?.read
                  ? 'black'
                  : 'gray.400'
              }
              onClick={
                companyPermissions?.update && companyPermissions?.read
                  ? enable
                  : undefined
              } // Only enables click if update permission exists
              cursor={companyPermissions?.update ? 'pointer' : 'not-allowed'} // Changes cursor based on permission
            />
          </Tooltip>
        ) : (
          <CloseIcon
            ml='5'
            fontSize='large'
            onClick={disable}
            cursor='pointer'
          />
        )}
      </Flex>

      <Formik
        initialValues={initialValues}
        validationSchema={UpdateCompanyFormValidationSchema}
        onSubmit={handleUpdate}
        enableReinitialize>
        {({ setFieldValue, isSubmitting }) => (
          <Form noValidate>
            <Flex direction='column'>
              <Card
                bgColor='gray.200'
                mt={12}>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='Name of the Company'
                    required={!disabled && true}
                    name='name'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <Flex>
                    <InputField
                      label='Company Code'
                      name='companyCode'
                      type='text'
                      variant='auth'
                      bgColor='gray.200'
                      width='xs'
                      value={companyData.company_code}
                      disabled
                    />
                  </Flex>
                </Flex>

                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='E-Mail Address'
                    required={!disabled && true}
                    name='email'
                    type='email'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <InputField
                    label='Phone Number'
                    required={!disabled && true}
                    name='phoneNumber'
                    type='number'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <SelectField
                    label='Status'
                    name='status'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    options={[
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'INACTIVE', label: 'Inactive' },
                    ]}
                    disabled={disabled}
                  />
                </Flex>
              </Card>

              <Text
                mt={12}
                fontSize='xl'
                fontWeight='700'>
                Postal Address :
              </Text>

              <Card
                bgColor='gray.200'
                mt={4}>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='Address Line 1'
                    required={!disabled && true}
                    name='addressLine1'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <InputField
                    label='Address Line 2'
                    name='addressLine2'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                </Flex>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='Landmarks'
                    required={!disabled && true}
                    name='landmarks'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <InputField
                    label='Pincode'
                    required={!disabled && true}
                    name='pincode'
                    type='number'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    onChange={(e) => {
                      setFieldValue('pincode', e.target.value);
                      e.target.value.length === 6 &&
                        GetPincode(e.target.value, setFieldValue);
                    }}
                    disabled={disabled}
                  />
                </Flex>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='City'
                    required={!disabled && true}
                    name='city'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable || disabled ? 'gray.200' : ''}
                    disabled={inpDisable || disabled}
                  />
                  <InputField
                    label='State'
                    required={!disabled && true}
                    name='state'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable || disabled ? 'gray.200' : ''}
                    disabled={inpDisable || disabled}
                  />
                  <InputField
                    label='Country'
                    required={!disabled && true}
                    name='country'
                    type='text'
                    variant='auth'
                    bgColor={inpDisable || disabled ? 'gray.200' : ''}
                    disabled={inpDisable || disabled}
                  />
                </Flex>
              </Card>

              <Text
                mt={12}
                fontSize='xl'
                fontWeight='700'>
                Other Details :
              </Text>

              <Card
                bgColor='gray.200'
                mt={4}>
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='PAN Number'
                    required={!disabled && true}
                    name='panNumber'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    placeholder='10 Character PAN Number'
                    onChange={UpperCaseChange}
                    maxLength='10'
                    disabled={disabled}
                  />
                  <InputField
                    label='GST Number'
                    required={!disabled && true}
                    name='gstNumber'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    placeholder='15 Character GST Number'
                    onChange={UpperCaseChange}
                    maxLength='15'
                    disabled={disabled}
                  />
                </Flex>
              </Card>

              {!disabled && (
                <Flex>
                  <Button
                    mt={20}
                    colorScheme='blue'
                    type='submit'
                    isLoading={isSubmitting}>
                    Update Company
                  </Button>
                </Flex>
              )}
            </Flex>
          </Form>
        )}
      </Formik>
    </>
  );
}
